// Persistent piece store in the Origin Private File System.
// Layout: <root>/<key>/meta.json + one file per verified piece (`p<index>`). A piece file is only written after its
// SHA-256 matched the manifest; on resume the engine re-hashes every candidate before counting it, so a torn
// write after a crash is detected and re-fetched. The directory key is a hash, never the file name.
import { TransferFailure, MESSAGES } from '../errors.js';
import { digestHex } from '../sha256.js';

export const OPFS_ROOT = 'solred-hybrid-v1';

export function opfsSupported() {
  return typeof navigator !== 'undefined' && !!navigator.storage && typeof navigator.storage.getDirectory === 'function' &&
    typeof globalThis.FileSystemFileHandle !== 'undefined' && 'createWritable' in globalThis.FileSystemFileHandle.prototype;
}

async function rootDir() {
  const top = await navigator.storage.getDirectory();
  return top.getDirectoryHandle(OPFS_ROOT, { create: true });
}

export async function storeKey(manifest) {
  const id = `${manifest.resourceId}\u0000${manifest.version}\u0000${manifest.sha256}`;
  return 'k' + (await digestHex(new TextEncoder().encode(id))).slice(0, 40);
}

function isQuota(e) {
  return e && (e.name === 'QuotaExceededError' || e.code === 22);
}

async function readMeta(dir) {
  try {
    const f = await (await dir.getFileHandle('meta.json')).getFile();
    return JSON.parse(await f.text());
  } catch { return null; }
}

async function writeJson(dir, name, value) {
  const w = await (await dir.getFileHandle(name, { create: true })).createWritable();
  await w.write(JSON.stringify(value));
  await w.close();
}

/** Bytes of an INACTIVE entry. Files that vanish meanwhile (Chromium `.crswap`, a concurrent removal) count as 0. */
async function dirBytes(dir) {
  let n = 0;
  for await (const [, h] of dir.entries()) {
    if (h.kind !== 'file') continue;
    try { n += (await h.getFile()).size; } catch { /* gone between listing and reading */ }
  }
  return n;
}

// ---------- active ownership (browser-opfs-cache-lease-v1) ----------
// Every key owned by a live engine holds a same-origin Web Locks *shared* lease for its whole lifetime, so a sweep in
// any page of this origin can see it (navigator.locks.query) and can only delete a directory while holding the
// *exclusive* lease on that key, requested `ifAvailable` (never waits, never steals). A new owner's shared request
// waits until such a deletion has finished. Leases end on release() or when the owning page goes away.
export const LEASE_PREFIX = 'solred-opfs-active:';
export const SWEEP_LEASE = 'solred-opfs-sweep';

/** The origin's LockManager, or null when cross-page coordination is unavailable (then nothing is auto-evicted). */
export function lockManager() {
  const l = typeof navigator !== 'undefined' ? navigator.locks : null;
  return l && typeof l.request === 'function' && typeof l.query === 'function' ? l : null;
}

/** Store keys owned by a live engine on this page. Never removed by a sweep (download bytes, not optional cache). */
const ACTIVE = new Map(); // key → { n, granted: Promise, unlock: fn|null, closed }
export const activeKeys = () => new Set(ACTIVE.keys());

/**
 * Take (or join) this page's lease on `key`. Resolves once the cross-page shared lease is granted; returns an
 * idempotent release. Local reference counts keep the lease while any holder on this page remains. If the platform
 * has a LockManager but refuses the request, the local count is undone and a `storage` failure is thrown before any
 * directory is touched (the engine can then use its native fallback). Without a LockManager the hold is local only.
 */
async function hold(key) {
  let e = ACTIVE.get(key);
  if (!e) {
    e = { n: 0, unlock: null, closed: false, granted: Promise.resolve(), lockDone: Promise.resolve() };
    ACTIVE.set(key, e);
    const L = lockManager();
    if (L) {
      const entry = e;
      let grant, deny;
      entry.granted = new Promise((res, rej) => { grant = res; deny = rej; });
      entry.granted.catch(() => {}); // observed by every joining holder below
      // Settles when the shared lease is gone (released or refused): removal waits for it before asking for exclusive.
      entry.lockDone = Promise.resolve().then(() => L.request(LEASE_PREFIX + key, { mode: 'shared' }, () => new Promise((done) => {
        entry.unlock = done;
        grant();
        if (entry.closed) done(); // released before the grant arrived
      }))).then(() => {}, (err) => { deny(err); });
    }
  }
  e.n++;
  const entry = e;
  let held = true;
  const release = () => {
    if (!held) return;
    held = false;
    if (--entry.n > 0) return;
    if (ACTIVE.get(key) === entry) ACTIVE.delete(key);
    entry.closed = true;
    if (entry.unlock) entry.unlock();
  };
  try { await entry.granted; } catch (err) {
    release();
    throw new TransferFailure('storage', MESSAGES.storage, err);
  }
  return { release, entry };
}

/**
 * End this holder's ownership, then delete the entry only if nobody owns it any more: no other holder on this page
 * and the exclusive lease obtainable without waiting (no other page holds or awaits it). Our own shared lease is fully
 * released first, so this never waits on itself. Without a LockManager other owners cannot be ruled out: keep bytes.
 * @returns {Promise<boolean>} whether the directory was deleted
 */
async function releaseAndRemove(key, root, owned) {
  owned.release();
  if (ACTIVE.has(key)) return false;
  const L = lockManager();
  if (!L) return false;
  await owned.entry.lockDone;
  if (ACTIVE.has(key)) return false;
  return L.request(LEASE_PREFIX + key, { mode: 'exclusive', ifAvailable: true }, async (lease) => {
    if (!lease || ACTIVE.has(key)) return false; // another owner (any page) remains: retain its bytes
    try { await root.removeEntry(key, { recursive: true }); return true; } catch { return false; }
  });
}

/** Keys held or requested by an owner in ANY page of this origin (plus this page's local holders). */
async function leasedKeys(L) {
  const out = new Set(ACTIVE.keys());
  const snap = await L.query();
  for (const l of [...(snap.held || []), ...(snap.pending || [])]) if (l.name && l.name.startsWith(LEASE_PREFIX)) out.add(l.name.slice(LEASE_PREFIX.length));
  return out;
}

/**
 * Pure sweep plan. Active keys are never touched. Expired (TTL by last real use) and meta-less entries go first;
 * then, while the optional cache is over quota, the least recently used entries that are not `spare` go.
 * Quota counts only removable (inactive) entries: active download bytes are not part of the cache budget.
 * @returns {string[]} keys to delete
 */
export function planSweep(entries, { ttlSeconds, quotaBytes, active = new Set(), spare = new Set(), now = Date.now() } = {}) {
  const out = [];
  const inactive = entries.filter((e) => !active.has(e.key));
  const alive = [];
  for (const e of inactive) {
    const age = e.meta && e.meta.updatedAt ? (now - e.meta.updatedAt) / 1000 : Infinity;
    if (!e.meta || (Number.isFinite(ttlSeconds) && ttlSeconds > 0 && age > ttlSeconds)) out.push(e.key); else alive.push(e);
  }
  if (Number.isFinite(quotaBytes) && quotaBytes >= 0) {
    let total = alive.reduce((n, e) => n + (e.bytes || 0), 0);
    for (const e of alive.filter((x) => !spare.has(x.key)).sort((a, b) => (a.meta.updatedAt || 0) - (b.meta.updatedAt || 0))) {
      if (total <= quotaBytes) break;
      out.push(e.key); total -= e.bytes || 0;
    }
  }
  return out;
}

/**
 * Apply `planSweep` to this origin's OPFS cache. `keep`: extra keys protected from quota eviction (not from TTL).
 * Active directories (leased by any page of this origin) are excluded before their mutable files are listed. Each
 * deletion happens under that key's exclusive lease, requested without waiting, and rechecks local ownership.
 * Without Web Locks (or while another page is sweeping) nothing is evicted: `skipped` says why.
 * @returns {Promise<{removed:number, removedKeys:string[], skipped?:'unsupported'|'no-locks'|'busy'}>}
 */
export async function sweepOpfs({ ttlSeconds, quotaBytes, keep, now = Date.now() } = {}) {
  if (!opfsSupported()) return { removed: 0, removedKeys: [], skipped: 'unsupported' };
  const L = lockManager();
  if (!L) return { removed: 0, removedKeys: [], skipped: 'no-locks' };
  return L.request(SWEEP_LEASE, { mode: 'exclusive', ifAvailable: true }, async (sweepLease) => {
    if (!sweepLease) return { removed: 0, removedKeys: [], skipped: 'busy' };
    const root = await rootDir();
    const active = await leasedKeys(L);
    const entries = [];
    for await (const [name, h] of root.entries()) {
      if (h.kind !== 'directory' || active.has(name)) continue; // never list an active download's files
      try { entries.push({ key: name, meta: await readMeta(h), bytes: await dirBytes(h) }); } catch { /* vanished */ }
    }
    const planned = planSweep(entries, { ttlSeconds, quotaBytes, active, spare: new Set([].concat(keep || [])), now });
    const removedKeys = [];
    for (const k of planned) {
      const removed = await L.request(LEASE_PREFIX + k, { mode: 'exclusive', ifAvailable: true }, async (lease) => {
        if (!lease || ACTIVE.has(k)) return false; // an owner appeared meanwhile: keep it
        try { await root.removeEntry(k, { recursive: true }); return true; } catch { return false; }
      });
      if (removed) removedKeys.push(k);
    }
    return { removed: removedKeys.length, removedKeys };
  });
}

/** Cache directories of this origin with their metadata (no file names are stored). */
export async function listCacheEntries() {
  if (!opfsSupported()) return [];
  const root = await rootDir();
  const out = [];
  for await (const [name, h] of root.entries()) if (h.kind === 'directory') out.push({ key: name, meta: await readMeta(h) });
  return out;
}

/** Free space the browser reports for this origin (null when unknown). */
export async function freeBytes() {
  try {
    const { quota, usage } = await navigator.storage.estimate();
    if (Number.isFinite(quota) && Number.isFinite(usage)) return Math.max(0, quota - usage);
  } catch { /* unknown */ }
  return null;
}

/**
 * @param {object} manifest
 * @param {{ active?: boolean, touch?: boolean }} [o] active: hold the cross-page lease (protect the key from every
 *   same-origin sweep) until `release()` or `remove()`;
 *   touch: false for read-only cache seeding (does not extend the entry's lifetime).
 */
export async function createOpfsStore(manifest, { active = true, touch: doTouch = true } = {}) {
  if (!opfsSupported()) throw new TransferFailure('unsupported', MESSAGES.unsupported);
  const key = await storeKey(manifest);
  const owned = active ? await hold(key) : { release: () => {}, entry: { lockDone: Promise.resolve() } };
  const release = owned.release;
  let root, dir;
  try {
    root = await rootDir();
    dir = await root.getDirectoryHandle(key, { create: true });
  } catch (e) { release(); throw e; }
  const handles = new Map();
  let meta = null;

  async function touch(extra) {
    meta = { ...(meta || {}), resourceId: manifest.resourceId, version: manifest.version, sha256: manifest.sha256, sizeBytes: manifest.sizeBytes, pieceLength: manifest.pieceLength, updatedAt: Date.now(), ...extra };
    try { await writeJson(dir, 'meta.json', meta); } catch (e) { if (isQuota(e)) throw new TransferFailure('storage-write', MESSAGES['storage-write'], e); throw e; }
  }

  return {
    kind: 'opfs',
    resumable: true,
    key,
    /** Candidate pieces from a previous visit (right length only; the engine re-verifies their hashes). */
    async open() {
      meta = await readMeta(dir);
      if (!doTouch) { // read-only: a different version or missing meta is not ours to repair
        if (!meta || meta.sha256 !== manifest.sha256 || meta.pieceLength !== manifest.pieceLength) return [];
      } else if (meta && (meta.sha256 !== manifest.sha256 || meta.pieceLength !== manifest.pieceLength)) {
        for await (const [name] of dir.entries()) await dir.removeEntry(name);
        meta = null;
      }
      if (doTouch) await touch({ createdAt: meta?.createdAt || Date.now() });
      const found = [];
      for await (const [name, h] of dir.entries()) {
        const m = /^p(\d+)$/.exec(name);
        if (!m || h.kind !== 'file') continue;
        const i = Number(m[1]), p = manifest.pieces[i];
        if (!p) { await dir.removeEntry(name); continue; }
        if ((await h.getFile()).size === p.length) { found.push(i); handles.set(i, h); } else await dir.removeEntry(name);
      }
      return found.sort((a, b) => a - b);
    },
    async put(index, bytes) {
      try {
        const h = await dir.getFileHandle(`p${index}`, { create: true });
        const w = await h.createWritable();
        await w.write(bytes);
        await w.close();
        handles.set(index, h);
      } catch (e) {
        if (isQuota(e)) throw new TransferFailure('storage-write', MESSAGES['storage-write'], e);
        throw e;
      }
    },
    async get(index) {
      const h = handles.get(index) || (await dir.getFileHandle(`p${index}`).catch(() => null));
      if (!h) return null;
      return new Uint8Array(await (await h.getFile()).arrayBuffer());
    },
    async drop(index) { handles.delete(index); await dir.removeEntry(`p${index}`).catch(() => {}); },
    async complete() { await touch({ complete: true }); },
    /** Cached bytes failed re-verification: never seed this entry again until a download completes it. Keeps updatedAt. */
    async markIncomplete() { if (meta) { meta = { ...meta, complete: false }; await writeJson(dir, 'meta.json', meta).catch(() => {}); } },
    /** The engine no longer owns this key (page left, transfer destroyed); sweeps may consider it again. */
    release,
    /** Disk-backed Blob made of the piece Files (no copy of the bytes into memory). */
    async blob(mime) {
      const parts = [];
      for (let i = 0; i < manifest.pieces.length; i++) {
        const h = handles.get(i) || (await dir.getFileHandle(`p${i}`).catch(() => null));
        if (!h) throw new TransferFailure('file-integrity', MESSAGES['file-integrity']);
        parts.push(await h.getFile());
      }
      return new Blob(parts, { type: mime || manifest.mime });
    },
    /** End ownership and delete the entry unless another owner (this page or another) still holds it. */
    async remove() { handles.clear(); return releaseAndRemove(key, root, owned); },
  };
}
